/**
 * Usage Reports Component
 *
 * Displays usage statistics, cost reports, and resource consumption
 * across bots and tenants.
 */

import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { getApiClient, isSuccess } from '../../api/client';

// =============================================================================
// Types
// =============================================================================

interface UsageSummary {
  period: {
    start: string;
    end: string;
  };
  totals: {
    chatCompletions: number;
    embeddings: number;
    ttsRequests: number;
    sttRequests: number;
    imageGenerations: number;
    webSearches: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    estimatedCost: number;
  };
  breakdown: Array<{
    date: string;
    chatCompletions: number;
    embeddings: number;
    ttsRequests: number;
    sttRequests: number;
    imageGenerations: number;
    webSearches: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    estimatedCost: number;
  }>;
}

interface CostReport {
  period: {
    start: string;
    end: string;
  };
  totalCost: number;
  breakdown: Array<{
    category: string;
    cost: number;
    percentage: number;
  }>;
  byProvider: Array<{
    provider: string;
    cost: number;
    requests: number;
  }>;
  byModel: Array<{
    model: string;
    cost: number;
    tokens: number;
  }>;
}

interface ProjectedCost {
  projectedCost: number;
  averageDailyCost: number;
  sampledDays: number;
  confidence: 'low' | 'medium' | 'high';
}

// =============================================================================
// Component
// =============================================================================

export function UsageReports() {
  useAuth(); // Ensure user is authenticated
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [costReport, setCostReport] = useState<CostReport | null>(null);
  const [projectedCost, setProjectedCost] = useState<ProjectedCost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const apiClient = getApiClient();

      const [usageRes, costRes] = await Promise.all([
        apiClient.get<UsageSummary>(`/reports/usage?period=${period}`),
        apiClient.get<CostReport>('/reports/costs'),
      ]);

      // Fetch projected cost separately to handle errors gracefully
      let projectedRes;
      try {
        projectedRes = await apiClient.get<ProjectedCost>('/reports/projected-costs');
      } catch {
        // Ignore errors for projected costs
      }

      if (isSuccess(usageRes)) {
        setUsageSummary(usageRes.data);
      }
      if (isSuccess(costRes)) {
        setCostReport(costRes.data);
      }
      if (projectedRes && isSuccess(projectedRes)) {
        setProjectedCost(projectedRes.data);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load reports');
      console.error('Reports fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high':
        return 'text-green-400';
      case 'medium':
        return 'text-yellow-400';
      case 'low':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-discord-blurple border-t-transparent" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Usage Reports</h1>
            <p className="mt-1 text-sm text-gray-400">
              Resource consumption and cost analysis
            </p>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as typeof period)}
              className="rounded-md border border-discord-light bg-discord-dark px-3 py-2 text-sm text-white focus:border-discord-blurple focus:outline-none"
            >
              <option value="day">Last 24 Hours</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="year">Last Year</option>
            </select>
            <button
              onClick={fetchReports}
              className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/80"
            >
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-500/10 border border-red-500/20 p-4 text-red-400">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Total Cost</div>
            <div className="mt-1 text-2xl font-bold text-white">
              {formatCurrency(usageSummary?.totals?.estimatedCost ?? 0)}
            </div>
            {projectedCost && (
              <div className="mt-2 text-xs text-gray-400">
                Projected: {formatCurrency(projectedCost.projectedCost)}/mo
                <span className={`ml-1 ${getConfidenceColor(projectedCost.confidence)}`}>
                  ({projectedCost.confidence} confidence)
                </span>
              </div>
            )}
          </div>

          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Total Tokens</div>
            <div className="mt-1 text-2xl font-bold text-white">
              {formatNumber(usageSummary?.totals?.totalTokens ?? 0)}
            </div>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-green-400">
                {formatNumber(usageSummary?.totals?.inputTokens ?? 0)} in
              </span>
              <span className="text-blue-400">
                {formatNumber(usageSummary?.totals?.outputTokens ?? 0)} out
              </span>
            </div>
          </div>

          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Chat Completions</div>
            <div className="mt-1 text-2xl font-bold text-white">
              {formatNumber(usageSummary?.totals?.chatCompletions ?? 0)}
            </div>
          </div>

          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Voice Operations</div>
            <div className="mt-1 text-2xl font-bold text-white">
              {formatNumber((usageSummary?.totals?.ttsRequests ?? 0) + (usageSummary?.totals?.sttRequests ?? 0))}
            </div>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-purple-400">
                {formatNumber(usageSummary?.totals?.ttsRequests ?? 0)} TTS
              </span>
              <span className="text-orange-400">
                {formatNumber(usageSummary?.totals?.sttRequests ?? 0)} STT
              </span>
            </div>
          </div>
        </div>

        {/* Cost Breakdown */}
        {costReport && (
          <div className="grid gap-6 lg:grid-cols-2">
            {/* By Provider */}
            <div className="rounded-lg bg-discord-dark p-6">
              <h2 className="mb-4 text-lg font-semibold text-white">Cost by Provider</h2>
              {(costReport.byProvider?.length ?? 0) > 0 ? (
                <div className="space-y-3">
                  {costReport.byProvider?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div>
                        <div className="text-white">{item.provider}</div>
                        <div className="text-xs text-gray-400">
                          {formatNumber(item.requests)} requests
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-white">{formatCurrency(item.cost)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-4 text-center text-gray-400">No data available</div>
              )}
            </div>

            {/* By Model */}
            <div className="rounded-lg bg-discord-dark p-6">
              <h2 className="mb-4 text-lg font-semibold text-white">Cost by Model</h2>
              {(costReport.byModel?.length ?? 0) > 0 ? (
                <div className="space-y-3">
                  {costReport.byModel?.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div>
                        <div className="text-white">{item.model}</div>
                        <div className="text-xs text-gray-400">
                          {formatNumber(item.tokens)} tokens
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-white">{formatCurrency(item.cost)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-4 text-center text-gray-400">No data available</div>
              )}
            </div>
          </div>
        )}

        {/* Usage Breakdown Table */}
        {usageSummary && (usageSummary.breakdown?.length ?? 0) > 0 && (
          <div className="rounded-lg bg-discord-dark p-6">
            <h2 className="mb-4 text-lg font-semibold text-white">Daily Breakdown</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-discord-light text-left text-sm text-gray-400">
                    <th className="pb-3 pr-4">Date</th>
                    <th className="pb-3 pr-4 text-right">Chat</th>
                    <th className="pb-3 pr-4 text-right">Embeddings</th>
                    <th className="pb-3 pr-4 text-right">TTS</th>
                    <th className="pb-3 pr-4 text-right">STT</th>
                    <th className="pb-3 pr-4 text-right">Images</th>
                    <th className="pb-3 pr-4 text-right">Tokens</th>
                    <th className="pb-3 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {usageSummary.breakdown?.map((day, idx) => (
                    <tr key={idx} className="border-b border-discord-light/50">
                      <td className="py-3 pr-4 text-white">
                        {new Date(day.date).toLocaleDateString()}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-300">
                        {formatNumber(day.chatCompletions)}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-300">
                        {formatNumber(day.embeddings)}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-300">
                        {formatNumber(day.ttsRequests)}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-300">
                        {formatNumber(day.sttRequests)}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-300">
                        {formatNumber(day.imageGenerations)}
                      </td>
                      <td className="py-3 pr-4 text-right text-gray-300">
                        {formatNumber(day.totalTokens)}
                      </td>
                      <td className="py-3 text-right font-medium text-white">
                        {formatCurrency(day.estimatedCost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-discord-light">
                    <td className="pt-3 pr-4 font-semibold text-white">Total</td>
                    <td className="pt-3 pr-4 text-right font-semibold text-white">
                      {formatNumber(usageSummary.totals?.chatCompletions ?? 0)}
                    </td>
                    <td className="pt-3 pr-4 text-right font-semibold text-white">
                      {formatNumber(usageSummary.totals?.embeddings ?? 0)}
                    </td>
                    <td className="pt-3 pr-4 text-right font-semibold text-white">
                      {formatNumber(usageSummary.totals?.ttsRequests ?? 0)}
                    </td>
                    <td className="pt-3 pr-4 text-right font-semibold text-white">
                      {formatNumber(usageSummary.totals?.sttRequests ?? 0)}
                    </td>
                    <td className="pt-3 pr-4 text-right font-semibold text-white">
                      {formatNumber(usageSummary.totals?.imageGenerations ?? 0)}
                    </td>
                    <td className="pt-3 pr-4 text-right font-semibold text-white">
                      {formatNumber(usageSummary.totals?.totalTokens ?? 0)}
                    </td>
                    <td className="pt-3 text-right font-semibold text-discord-blurple">
                      {formatCurrency(usageSummary.totals?.estimatedCost ?? 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Additional Stats */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Embeddings</div>
            <div className="mt-1 text-2xl font-bold text-white">
              {formatNumber(usageSummary?.totals?.embeddings ?? 0)}
            </div>
          </div>
          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Image Generations</div>
            <div className="mt-1 text-2xl font-bold text-white">
              {formatNumber(usageSummary?.totals?.imageGenerations ?? 0)}
            </div>
          </div>
          <div className="rounded-lg bg-discord-dark p-4">
            <div className="text-sm text-gray-400">Web Searches</div>
            <div className="mt-1 text-2xl font-bold text-white">
              {formatNumber(usageSummary?.totals?.webSearches ?? 0)}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default UsageReports;
